import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { TimesheetReport } from "domain/entities/timesheet-report";
import React from "react";
import logo from "../../../assets/logo.png";
import { useAuthStore } from "presentation/stores/use-auth-store";
// import { Employee } from "domain/entities/employee";
// import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";

const MAX_ROWS_PER_PAGE = 24;

const styles = StyleSheet.create({
  page: { padding: 60, fontSize: 10, fontFamily: "Helvetica" },

  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },

  logo: { width: 130, height: 15 },

  title: {
    fontSize: 14,
    textAlign: "center",
    marginVertical: 10,
    fontWeight: "bold",
  },

  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },

  table: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#000",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderColor: "#000",
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#ccc",
  },

  tableCell: {
    flex: 1,
    padding: 4,
    borderRightWidth: 1,
    borderColor: "#ccc",
    display: "flex",
    alignItems: "center",
  },

  tableCellNoBorder: {
    flex: 1,
    padding: 4,
  },

  descriptionCell: {
    flex: 2.5,
    padding: 4,
    borderRightWidth: 1,
    borderColor: "#ccc",
    justifyContent: "center",
    display: "flex",
  },

  lastCell: {
    borderRightWidth: 0,
  },

  bold: {
    fontWeight: "bold",
  },

  centerCell: {
    textAlign: "center",
  },

  logoUnderline: {
    borderBottomWidth: 1,
    borderColor: "#000",
    width: "100%",
  },

  backgroundGray: {
    backgroundColor: "#f0f0f0",
  },

  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 5,
  },

  footer: {
    position: "absolute",
    bottom: 30,
    left: 0,
    right: 60,
    textAlign: "right",
    fontSize: 10,
  },
});

type Props = {
  data: TimesheetReport;
  isSuportService: boolean;
};


const TimesheetReportPDF: React.FC<Props> = ({ data, isSuportService }) => {

  const { userEmployee } = useAuthStore();

  const totals = {
    abscence: 0,
    allocation: 0,
    support: 0,
    abscenceDays: 0,
    allocationDays: 0,
  };

  const rows = data.days.flatMap((d) =>
    d.activities.map((a) => {
      const isSupportOrDeslocation =
        a.name.toLowerCase().includes("prevenção") ||
        a.name.toLowerCase().includes("deslocação");

      if (a.type === "Abscence") totals.abscence += a.hours;
      if (a.type === "PO" && !isSupportOrDeslocation)
        totals.allocation += a.hours;
      if (a.type === "PO" && isSupportOrDeslocation) totals.support += 1;

      return {
        day: d.day,
        description: a.name,
        abscence: a.type === "Abscence" ? a.hours : "",
        allocation: a.type === "PO" && !isSupportOrDeslocation ? a.hours : "",
        support: a.type === "PO" && isSupportOrDeslocation ? 1 : "",
      };
    })
  );

  const pages = [];
  for (let i = 0; i < rows.length; i += MAX_ROWS_PER_PAGE) {
    pages.push(rows.slice(i, i + MAX_ROWS_PER_PAGE));
  }

  totals.abscenceDays = parseFloat((totals.abscence / (userEmployee?.workHoursPerDay ?? 8)).toFixed(2));
  totals.allocationDays = parseFloat((totals.allocation / (userEmployee?.workHoursPerDay ?? 8)).toFixed(2));

  return (
    <Document>
      {pages.map((pageRows, pageIndex) => (
        <Page style={styles.page} size="A4" key={pageIndex}>
          {/* Header */}
          <View style={styles.headerRow}>
            <Image src={logo} style={styles.logo} />
          </View>
          <View style={styles.logoUnderline} />

          <Text style={styles.title}>
            Mapa de Horas – {String(data.month).padStart(2, "0")}/{data.year}
          </Text>

          {/* Info */}
          <View style={styles.infoRow}>
            <View>
              <Text>
                <Text style={{ fontWeight: "bold" }}>Colaborador: </Text>
                {data.name}
              </Text>
              <Text>
                <Text style={{ fontWeight: "bold" }}>E-mail: </Text>
                {data.email}
              </Text>
              <Text>
                <Text style={{ fontWeight: "bold" }}>Manager: </Text>
                {data.managerName}
              </Text>
              <Text>
                <Text style={{ fontWeight: "bold" }}>E-mail manager: </Text>
                {data.managerEmail ?? "SEM MANAGER"}
              </Text>
            </View>
            <View>
              <Text>
                <Text style={{ fontWeight: "bold" }}>Versão: </Text>
                {data.version}
              </Text>
              <Text>
                <Text style={{ fontWeight: "bold" }}>Status: </Text>
                {data.status}
              </Text>
            </View>
          </View>

          {/* Table */}
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, styles.bold, styles.centerCell]}>
                Dia
              </Text>
              <Text style={[styles.descriptionCell, styles.bold]}>
                Descrição
              </Text>
              <Text style={[styles.tableCell, styles.bold, styles.centerCell]}>
                Ausências
              </Text>
              <Text style={[styles.tableCell, styles.bold, styles.centerCell]}>
                Alocações
              </Text>
              {isSuportService && (
                <Text
                  style={[
                    styles.tableCell,
                    styles.bold,
                    styles.lastCell,
                    styles.centerCell,
                  ]}
                >
                  Prevenção ou Deslocação
                </Text>
              )}
            </View>

            {/* Table Rows */}
            {pageRows.map((row, index) => (
              <View style={styles.tableRow} key={index}>
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {row.day}
                </Text>
                <Text style={styles.descriptionCell}>{row.description}</Text>
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {row.abscence}
                </Text>
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {row.allocation}
                </Text>
                {isSuportService && (
                  <Text
                    style={[
                      styles.tableCell,
                      styles.lastCell,
                      styles.centerCell,
                    ]}
                  >
                    {row.support}
                  </Text>
                )}
              </View>
            ))}

            {/* Totals apenas na última página */}
            {pageIndex === pages.length - 1 && (
              <>
                <View style={styles.tableRow}>
                  <Text
                    style={[styles.tableCellNoBorder, styles.backgroundGray]}
                  />
                  <Text
                    style={[styles.descriptionCell, styles.backgroundGray]}
                  />
                  <Text style={[styles.tableCell, styles.centerCell]}>
                    {totals.abscence}
                  </Text>
                  <Text style={[styles.tableCell, styles.centerCell]}>
                    {totals.allocation}
                  </Text>
                  {isSuportService && (
                    <Text
                      style={[styles.tableCellNoBorder, styles.backgroundGray]}
                    />
                  )}
                </View>

                <View style={styles.tableRow}>
                  <Text
                    style={[styles.tableCellNoBorder, styles.backgroundGray]}
                  />
                  <Text
                    style={[styles.descriptionCell, styles.backgroundGray]}
                  />
                  <Text style={[styles.tableCell, styles.centerCell]}>
                    {totals.abscenceDays} dias
                  </Text>
                  <Text style={[styles.tableCell, styles.centerCell]}>
                    {totals.allocationDays} dias
                  </Text>
                  {isSuportService && (
                    <Text
                      style={[
                        styles.tableCell,
                        styles.lastCell,
                        styles.centerCell,
                      ]}
                    >
                      {totals.support} dias
                    </Text>
                  )}
                </View>
              </>
            )}
          </View>

          <Text style={styles.footer}>
            Página {pageIndex + 1}/{pages.length}
          </Text>
        </Page>
      ))}
    </Document>
  );
};

export default TimesheetReportPDF;
